import { useEffect, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';

/**
 * Renders the official Swiss QR-bill payment part as SVG using `swissqrbill`.
 * The library is imported dynamically so a generation error (e.g. an invalid
 * IBAN entered in settings) never breaks the page bundle - we show a hint
 * instead. The SVG it returns is library-generated and XML-escaped.
 */
/**
 * Eine freie Adresse in Strasse, Postleitzahl und Ort zerlegen.
 *
 * Ein Kunde hat EIN Feld für die Rechnungsadresse - der Zahlteil braucht drei.
 * Erkannt wird die schweizerische Schreibweise, bei der die letzte Zeile mit
 * der Postleitzahl beginnt ("8001 Zürich", "CH-8001 Zürich").
 *
 * Passt sie nicht in dieses Muster, kommt `null` zurück und der
 * Zahlungspflichtige bleibt weg. Eine halb geratene Adresse auf einem
 * Zahlteil ist schlechter als ein leeres Feld: das leere kann man ausfüllen,
 * die falsche schickt die Zahlung ins Nichts.
 *
 * @returns {{address: string, zip: string, city: string, country: string}|null}
 */
export function zerlegeAdresse(roh) {
  const zeilen = String(roh || '')
    .split(/[\n,]/)
    .map((z) => z.trim())
    .filter(Boolean);
  if (zeilen.length < 2) {
    return null;
  }
  const letzte = zeilen[zeilen.length - 1];
  const m = /^(?:CH[- ])?(\d{4})\s+(.+)$/i.exec(letzte);
  if (!m) {
    return null;
  }
  const strasse = zeilen.slice(0, -1).join(', ');
  if (!strasse) {
    return null;
  }
  return { address: strasse, zip: m[1], city: m[2], country: 'CH' };
}

export function SwissQrBill({ settings, invoice, customer, currency }) {
  const { t } = useTranslation();
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);

  // The Swiss QR-bill standard only supports CHF and EUR. For any other
  // document currency the payment part is OMITTED entirely (spec §7) -
  // printing a CHF QR for a USD invoice would be scannable but wrong.
  const isUnsupportedCurrency = Boolean(currency && currency !== 'CHF' && currency !== 'EUR');

  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setError(null);

    if (isUnsupportedCurrency) {
      return undefined;
    }

    if (!settings?.iban || !settings?.companyName) {
      setError('missing');
      return undefined;
    }

    const creditor = {
      account: String(settings.iban).replace(/\s+/g, ''),
      name: settings.companyName,
      address: settings.addressLine1 || settings.companyName,
      zip: settings.zip || '',
      city: settings.city || '',
      country: (settings.country || 'CH').toUpperCase(),
    };

    const data = {
      currency: currency === 'EUR' ? 'EUR' : 'CHF',
      amount: Number(invoice.totalAmount) || undefined,
      creditor,
      message: invoice.invoiceNumber ? `${t('invoice.print.heading')} ${invoice.invoiceNumber}` : undefined,
    };

    /* Der Zahlungspflichtige ist im Standard OPTIONAL, und das ist hier die
       bessere Antwort als eine erfundene Adresse.

       Vorher wurde bei fehlender Rechnungsadresse der Name als Adresse
       eingesetzt und ein Bindestrich als Ort - auf dem gedruckten Zahlteil
       stand dann "Acme Corporation / Acme Corporation / -". Das ist keine
       Kleinigkeit: der Zahlteil geht an den Kunden des Kunden, und dort sieht
       es aus, als hätte jemand ein Formular halb ausgefüllt.

       Lässt man das Feld weg, druckt die Bibliothek das dafür vorgesehene
       LEERE Feld mit Linien zum Ausfüllen. Genau dafür ist es da.

       Eine Adresse wird nur übernommen, wenn sie sich in Strasse, Postleitzahl
       und Ort zerlegen lässt - die Bibliothek verlangt alle drei, und geraten
       wird hier nichts. */
    const adresse = zerlegeAdresse(customer?.billingAddress);
    if (customer?.name && adresse) {
      data.debtor = { name: customer.name, ...adresse };
    }

    import('swissqrbill/svg')
      .then(({ SwissQRBill }) => {
        if (cancelled) {
          return;
        }
        try {
          const bill = new SwissQRBill(data);
          setSvg(bill.toString());
        } catch (err) {
          setError(err?.message || 'invalid');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('load');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [settings, invoice, customer, currency, isUnsupportedCurrency, t]);

  if (isUnsupportedCurrency) {
    return null;
  }
  if (error === 'missing') {
    return (
      <div className="qr-hint no-print">
        {t('invoice.print.qrMissing')}
      </div>
    );
  }
  if (error) {
    return (
      <div className="qr-hint no-print">
        {t('invoice.print.qrError')}
      </div>
    );
  }
  if (!svg) {
    return null;
  }
  // eslint-disable-next-line react/no-danger
  return <div className="qr-bill" dangerouslySetInnerHTML={{ __html: svg }} />;
}
